// services/account.js — the signed-in person's place in their team account.
//
// An account is an owner plus the people they invite. The server decides what
// each person may do (mars-backend/services/account) and enforces it on every
// request; this module only reads that answer so the menu, page guard and
// buttons can match it. Hiding something here never grants anything.
import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// --- shared cache, one fetch per page load ----------------------------------

let cache = { loaded: false, value: null };
let inflight = null;
const listeners = new Set();

const publish = (value) => {
  cache = { loaded: true, value: value || null };
  listeners.forEach((fn) => fn(cache.value));
};

/** Fill the cache from a response that already carried the summary. */
export const primeAccount = (value) => {
  if (value && typeof value === 'object') publish(value);
};

export const loadAccount = ({ force = false } = {}) => {
  if (cache.loaded && !force) return Promise.resolve(cache.value);
  if (inflight) return inflight;
  inflight = fetch(`${API_BASE}/api/account/me`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      publish(data && data.success ? data.data : null);
      return cache.value;
    })
    .catch(() => {
      // Unknown means "behave as before": the server still enforces.
      publish(null);
      return null;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

/** { account, loading } — account is null for a signed-out page or on error. */
export const useAccount = () => {
  const [state, setState] = useState(cache.loaded ? cache.value : undefined);
  useEffect(() => {
    let alive = true;
    const listener = (value) => { if (alive) setState(value); };
    listeners.add(listener);
    loadAccount().then(listener);
    return () => {
      alive = false;
      listeners.delete(listener);
    };
  }, []);
  return { account: state === undefined ? null : state, loading: state === undefined };
};

// --- questions the UI asks ---------------------------------------------------

export const isSubUser = (account) => Boolean(account && account.role && account.role !== 'owner');
export const isTeamAdmin = (account) => Boolean(account && (account.role === 'owner' || account.role === 'co_admin'));
export const isRestrictedMember = (account) => Boolean(account && account.role === 'member');

export const featureLevel = (account, key) => {
  if (!isRestrictedMember(account)) return 'edit';
  return account.permissions?.features?.[key] || 'none';
};

export const canUse = (account, key) => featureLevel(account, key) !== 'none';
export const canEdit = (account, key) => featureLevel(account, key) === 'edit';
export const canExport = (account) => !isRestrictedMember(account) || account.permissions?.export === true;
export const canAddBrand = (account, kind) =>
  !isRestrictedMember(account) || account.permissions?.brands?.[kind]?.add === true;

const pathMatches = (pathname, page) =>
  String(pathname || '').toLowerCase() === String(page).toLowerCase();

/** The feature that owns a frontend path, or null. */
export const featureForPath = (account, pathname) =>
  (account?.features || []).find((f) => (f.pages || []).some((p) => pathMatches(pathname, p))) || null;

/**
 * Why this person may not open this page, or null if they may.
 * 'owner_only' | 'feature' | 'team_admin'
 */
export const pageBlockReason = (account, pathname) => {
  if (!account) return null;
  if (isSubUser(account) && (account.ownerOnlyPages || []).some((p) => pathMatches(pathname, p))) {
    return 'owner_only';
  }
  if (pathMatches(pathname, '/settings/team') && !isTeamAdmin(account)) return 'team_admin';
  if (isRestrictedMember(account)) {
    const feature = featureForPath(account, pathname);
    if (feature && featureLevel(account, feature.key) === 'none') return 'feature';
  }
  return null;
};

// --- Team API ----------------------------------------------------------------

const request = async (method, path, body) => {
  const res = await fetch(`${API_BASE}/api/account${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok || !data || data.success === false) {
    const error = new Error(data?.message || `Request failed (${res.status})`);
    error.status = res.status;
    error.code = data?.code;
    throw error;
  }
  return data;
};

export const teamApi = {
  options: () => request('GET', '/options').then((d) => d.data),
  members: () => request('GET', '/members').then((d) => d.data),
  invite: (body) => request('POST', '/members', body),
  updateMember: (id, body) => request('PATCH', `/members/${id}`, body),
  removeMember: (id) => request('DELETE', `/members/${id}`),
  resendInvite: (id) => request('POST', `/members/${id}/resend-invite`),
  permissions: (id) => request('GET', `/members/${id}/permissions`).then((d) => d.data),
  savePermissions: (id, body) => request('PUT', `/members/${id}/permissions`, body),
  bulk: (body) => request('POST', '/members/bulk', body),
  templates: () => request('GET', '/templates').then((d) => d.data),
  createTemplate: (body) => request('POST', '/templates', body),
  updateTemplate: (id, body) => request('PUT', `/templates/${id}`, body),
  deleteTemplate: (id) => request('DELETE', `/templates/${id}`),
  activity: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return request('GET', `/activity${qs ? `?${qs}` : ''}`).then((d) => d.data);
  },
};

// --- invite link + first sign-in (public / auth API) -------------------------

const authRequest = async (method, path, body) => {
  const res = await fetch(`${API_BASE}/api/auth${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok || !data || data.success === false) {
    const error = new Error(data?.message || `Request failed (${res.status})`);
    error.status = res.status;
    error.code = data?.code;
    throw error;
  }
  return data;
};

export const inviteApi = {
  lookup: (code) => authRequest('GET', `/invite/${encodeURIComponent(code)}`),
  login: (code) => authRequest('POST', '/invite-login', { code }),
  setPassword: (newPassword, confirmPassword) => authRequest('POST', '/set-password', { newPassword, confirmPassword }),
};

/** Same rule the server applies (8+ chars, upper, lower, digit). */
export const passwordProblem = (password) => {
  if (!password || password.length < 8) return 'At least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Add an uppercase letter';
  if (!/[a-z]/.test(password)) return 'Add a lowercase letter';
  if (!/\d/.test(password)) return 'Add a number';
  return null;
};

// --- plain-English summary of a permission set ---------------------------------

export const describePermissions = (permissions, features = [], brandCounts = {}) => {
  if (!permissions) return '';
  const parts = [];
  const on = features.filter((f) => (permissions.features?.[f.key] || 'none') !== 'none');
  parts.push(on.length === features.length ? 'All features' : `${on.length} of ${features.length} features`);

  const kindLabel = { projects: 'projects', competitors: 'competitors', gaps: 'gaps' };
  const brandBits = ['projects', 'competitors', 'gaps'].map((kind) => {
    const b = permissions.brands?.[kind] || {};
    const seen = b.see === 'selected' ? `${brandCounts[kind] || 0} selected ${kindLabel[kind]}` : `all ${kindLabel[kind]}`;
    return seen;
  });
  parts.push(`sees ${brandBits.join(', ')}`);

  const adds = ['projects', 'competitors', 'gaps'].filter((k) => permissions.brands?.[k]?.add);
  parts.push(adds.length ? `can add ${adds.map((k) => kindLabel[k]).join(' & ')}` : 'cannot add brands');

  if (Array.isArray(permissions.verticals)) parts.push(permissions.verticals.join(', ') || 'no verticals');
  if (Array.isArray(permissions.countries)) {
    parts.push(permissions.countries.length > 3
      ? `${permissions.countries.length} countries`
      : permissions.countries.join(', '));
  }
  if (permissions.row_limit) parts.push(`${permissions.row_limit} rows`);
  if (permissions.export) parts.push('can export');
  return parts.join(' · ');
};
